import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WarningOctagon, MagnifyingGlass } from "@phosphor-icons/react";

const PRESENCE_COLOR = {
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  away: "bg-slate-400",
  offline: "bg-slate-400",
};

const ROLE_LABEL = {
  admin: "Admin",
  supervisor: "Supervisor",
  bdc_agent: "BDC",
  bdc: "BDC",
  sales: "Ventas",
  service: "Servicio",
  marketing: "Marketing",
};

export default function EscalateDialog({ open, onOpenChange, conversationId, onEscalated }) {
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setReason("");
      setQ("");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/available-agents");
        setAgents(data || []);
      } catch (e) {
        toast.error("Error cargando agentes");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const filtered = agents.filter((a) => {
    if (!q.trim()) return true;
    const s = `${a.name || ""} ${a.email || ""} ${a.role || ""} ${a.branch || ""}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  const submit = async () => {
    if (!selectedId) return;
    setSending(true);
    try {
      await api.post(`/conversations/${conversationId}/escalate`, {
        target_agent_id: selectedId,
        reason,
      });
      toast.success("Conversación escalada");
      onEscalated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al escalar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="escalate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WarningOctagon size={18} className="text-amber-500" />
            Escalar conversación
          </DialogTitle>
          <DialogDescription>
            Selecciona a quién enviar esta conversación. Se marcará como escalada y el agente recibirá una notificación.
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-2">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar agente por nombre, rol o sucursal..."
            className="pl-9"
            data-testid="escalate-search"
          />
        </div>

        <ScrollArea className="h-64 border border-border rounded-md">
          {loading && <div className="p-6 text-sm text-muted-foreground text-center">Cargando…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground text-center">Sin resultados</div>
          )}
          {filtered.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelectedId(a.id)}
              data-testid={`escalate-agent-${a.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border transition-colors flex items-center gap-3 ${
                selectedId === a.id ? "bg-accent" : "hover:bg-secondary"
              }`}
            >
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                  {a.name?.[0]?.toUpperCase() || "?"}
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background ${PRESENCE_COLOR[a.presence] || PRESENCE_COLOR.offline}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{a.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {ROLE_LABEL[a.role] || a.role} · {a.branch || "—"}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant="outline" className="text-[10px]">
                  {a.open_load} abiertas
                </Badge>
                {a.presence === "available" && (
                  <span className="text-[10px] text-emerald-500 font-medium">Disponible</span>
                )}
              </div>
            </button>
          ))}
        </ScrollArea>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Motivo (opcional)
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: Cliente requiere cotización SAP..."
            className="mt-1"
            data-testid="escalate-reason"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="escalate-cancel">
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!selectedId || sending} data-testid="escalate-confirm">
            {sending ? "Escalando…" : "Escalar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
